/**
 * RIS Viewer Modal Component
 *
 * Modal for displaying RIS documents with intelligent highlighting.
 * Features loading states, error handling, and toolbar actions.
 */

import React, { useEffect, useRef } from 'react';
import {
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { buildApiUrl } from '../config/api';
import { useRISViewer } from '../contexts/RISViewerContext';
import { sanitizeRisHtml } from '../utils/sanitize';

const RISViewerModal: React.FC = () => {
  const { state, closeViewer, refreshViewer } = useRISViewer();
  const contentRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = React.useState(false);

  // Scroll to first highlight when document loads
  useEffect(() => {
    if (state.data && contentRef.current) {
      const firstHighlight = contentRef.current.querySelector('.highlight-primary, .highlight-secondary');
      if (firstHighlight) {
        setTimeout(() => {
          firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [state.data]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && state.isOpen) {
        closeViewer();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [state.isOpen, closeViewer]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (state.isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [state.isOpen]);

  if (!state.isOpen) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeViewer();
    }
  };

  const handleOpenExternal = () => {
    // For KV documents, use the WKO source URL; for RIS docs, use the RIS URL
    const externalUrl = state.data?.metadata?.sourceUrl || state.url;
    if (externalUrl && /^https?:\/\//i.test(externalUrl)) {
      window.open(externalUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCopyContent = async () => {
    if (contentRef.current) {
      try {
        const text = contentRef.current.innerText;
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const renderLoading = () => (
    <div className="flex flex-col items-center justify-center h-96 space-y-4">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-blue-200 dark:border-blue-900 rounded-full animate-spin border-t-blue-600 dark:border-t-blue-400"></div>
      </div>
      <div className="text-center">
        <p className="text-gray-700 dark:text-gray-300 font-medium">Dokument wird geladen...</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Relevante Stellen werden identifiziert
        </p>
      </div>
    </div>
  );

  const renderError = () => (
    <div className="flex flex-col items-center justify-center h-96 space-y-4 p-6">
      <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
        <XMarkIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
      </div>
      <div className="text-center">
        <p className="text-gray-900 dark:text-gray-100 font-medium">Fehler beim Laden</p>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-md">
          {state.error || 'Das Dokument konnte nicht geladen werden.'}
        </p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={refreshViewer}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <ArrowPathIcon className="w-4 h-4" />
          <span>Erneut versuchen</span>
        </button>
        <button
          onClick={handleOpenExternal}
          className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center space-x-2"
        >
          <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          <span>Im Browser</span>
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    if (!state.data) return null;

    return (
      <div
        ref={contentRef}
        className="ris-viewer-content prose prose-sm max-w-none dark:prose-invert"
        dangerouslySetInnerHTML={{ __html: sanitizeRisHtml(state.data.html) }}
      />
    );
  };

  // Use insertedHighlightCount (actual visible highlights) if available, otherwise fall back to total
  const highlightCount = state.data?.insertedHighlightCount ?? state.data?.highlights?.highlights?.length ?? 0;
  const fromCache = state.data?.fromCache;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
              {state.data?.metadata?.title || 'RIS Dokument'}
            </h2>
            {state.data?.metadata && (
              <div className="flex items-center space-x-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                <span>{state.data.metadata.gericht}</span>
                <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                <span>{state.data.metadata.geschaeftszahl}</span>
                {state.data.metadata.datum && (
                  <>
                    <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                    <span>{state.data.metadata.datum}</span>
                  </>
                )}
              </div>
            )}
            {(state.data?.metadata?.gesetzesnummer || state.data?.metadata?.dokumentnummer || state.data?.metadata?.imRisSeit || state.data?.metadata?.entscheidungsart || state.data?.metadata?.rechtsgebiet || state.data?.metadata?.rechtssatznummer) && (
              <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                {state.data.metadata.gesetzesnummer && <span>Gesetzesnr. {state.data.metadata.gesetzesnummer}</span>}
                {state.data.metadata.dokumentnummer && <span>Dok. {state.data.metadata.dokumentnummer}</span>}
                {state.data.metadata.imRisSeit && <span>Im RIS seit {state.data.metadata.imRisSeit}</span>}
                {state.data.metadata.entscheidungsart && <span>Entscheidungsart: {state.data.metadata.entscheidungsart}</span>}
                {state.data.metadata.rechtsgebiet && <span>Rechtsgebiet: {state.data.metadata.rechtsgebiet.length > 80 ? state.data.metadata.rechtsgebiet.substring(0, 80) + '...' : state.data.metadata.rechtsgebiet}</span>}
                {state.data.metadata.rechtssatznummer && <span>RS {state.data.metadata.rechtssatznummer}</span>}
              </div>
            )}
          </div>

          <button
            onClick={closeViewer}
            className="ml-4 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Schließen"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Toolbar */}
        {state.data && (
          <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              {highlightCount > 0 && (
                <span className="flex items-center space-x-1">
                  <span className="w-3 h-3 rounded" style={{ backgroundColor: '#FDE047' }}></span>
                  <span>{highlightCount} relevante Stelle{highlightCount !== 1 ? 'n' : ''}</span>
                </span>
              )}
              {fromCache && (
                <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                  aus Cache
                </span>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={handleCopyContent}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                {copied ? (
                  <>
                    <CheckIcon className="w-4 h-4 text-green-600" />
                    <span>Kopiert</span>
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="w-4 h-4" />
                    <span>Kopieren</span>
                  </>
                )}
              </button>

              <button
                onClick={refreshViewer}
                disabled={state.isLoading}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <ArrowPathIcon className={`w-4 h-4 ${state.isLoading ? 'animate-spin' : ''}`} />
                <span>Aktualisieren</span>
              </button>

              {state.data?.pdfDownloadUrl && (
                <a
                  href={buildApiUrl(state.data.pdfDownloadUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  PDF
                </a>
              )}

              <button
                onClick={handleOpenExternal}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                <span>Im Browser</span>
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-white dark:bg-gray-800">
          {state.isLoading && renderLoading()}
          {state.error && !state.isLoading && renderError()}
          {state.data && !state.isLoading && !state.error && renderContent()}
        </div>

        {/* Footer with highlight legend */}
        {state.data && highlightCount > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center justify-center space-x-6 text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-2">
                <span className="w-4 h-4 rounded" style={{ backgroundColor: '#FDE047' }}></span>
                <span>Direkt relevant</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-4 h-4 rounded" style={{ backgroundColor: '#FDBA74' }}></span>
                <span>Wichtiger Kontext</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom styles for RIS content */}
      <style>{`
        .ris-viewer-content {
          font-family: 'Times New Roman', serif;
          font-size: 14px;
          line-height: 1.6;
        }
        .ris-viewer-content h1,
        .ris-viewer-content h2,
        .ris-viewer-content h3 {
          font-family: 'Times New Roman', serif;
          color: inherit;
        }
        .ris-viewer-content .ErlText {
          margin-bottom: 0.5em;
        }
        .ris-viewer-content .Titel {
          font-weight: bold;
          margin-top: 1em;
        }
        .ris-viewer-content table {
          font-size: 12px;
        }
        /* Override RIS document styles for dark mode */
        .dark .ris-viewer-content {
          color: #e5e7eb;
        }
        .dark .ris-viewer-content a {
          color: #60a5fa;
        }
      `}</style>
    </div>
  );
};

export default RISViewerModal;
